
// Commercial meters widget (DLMS ITRON via WG bridge, 10-min collector).
// Generic: pass `endpoint` (e.g. /scada/bolgrad/meters, /scada/luchky/meters).
// Compact tiles that fill the card — shows EVERY point in a cols×N grid.
// Refreshes every 60 s. Read-only (behind cabinet login).
import i18n from '@/i18n';
import { useEffect, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';

interface Meter {
  ktp: string;
  serial: number;
  aMinusWh: number | null;
  aPlusWh: number | null;
  pW: number | null;
  genTodayWh: number | null;
  clockMeter: string | null;
  status: string;
  ageSeconds: number | null;
}
interface Payload {
  meters: Meter[];
  summary: { count: number; online: number };
  ts: string;
}

const REFRESH_MS = 60000;
const COLS = 3;

function dotColor(m: Meter): string {
  if (m.status !== 'ok') return scada.textMuted;
  const age = m.ageSeconds;
  if (age == null) return scada.textMuted;
  if (age < 1200) return scada.state.ok;
  if (age < 3600) return scada.state.warning;
  return scada.state.alarm;
}
function ago(age: number | null): string {
  if (age == null) return '—';
  if (age < 90) return i18n.t('ak_scada_s', { n: age });
  if (age < 3600) return i18n.t('ak_scada_min', { n: Math.round(age / 60) });
  return i18n.t('ak_scada_h', { n: (age / 3600).toFixed(1) });
}

// накопичувальний регістр у МВт·год (3 знаки)
const mwh = (wh: number | null): string => (wh == null ? '—' : (wh / 1e6).toFixed(3));

export default function CommercialMetersWidget({ endpoint, cols = COLS }: { endpoint: string; cols?: number }) {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await authFetch(`${API_BASE_URL}${endpoint}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as Payload;
        if (alive) { setData(j); setErr(null); }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : 'fetch error');
      }
    };
    void load();
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [endpoint]);

  const sub = data
    ? i18n.t('ak_scada_online_direct_dlms_reading_ozd', { o: data.summary.online, c: data.summary.count })
    : i18n.t('ak_scada_direct_dlms_reading_ozd');

  return (
    <SectionCard fill title={i18n.t('ak_scada_commercial_metering_dlms')} subtitle={sub}>
      {err && !data && (
        <span style={{ color: scada.state.alarm, fontSize: 12 }}>{i18n.t('ak_scada_error', { e: err })}</span>
      )}
      {!data && !err && (
        <span style={{ color: scada.textMuted, fontSize: 12 }}>{i18n.t('ak_scada_loading')}</span>
      )}
      {data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridAutoRows: '1fr',
            gap: 7,
            flex: 1,
            width: '100%',
            minHeight: 0,
          }}
        >
          {data.meters.map((m, i) => {
            const isLast = i === data.meters.length - 1;
            const aloneOnRow = isLast && data.meters.length % cols === 1;
            const online = m.status === 'ok';
            const dominImport = (m.aPlusWh ?? 0) > (m.aMinusWh ?? 0);
            const faceWh = dominImport ? m.aPlusWh : m.aMinusWh;
            return (
              <div
                key={m.serial}
                style={{
                  background: scada.panel2,
                  border: `1px solid ${scada.border}`,
                  borderRadius: 8,
                  padding: '7px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 2,
                  gridColumn: aloneOnRow ? '2 / 3' : undefined,
                  opacity: online ? 1 : 0.7,
                }}
              >
                <div className="flex items-center justify-between" style={{ gap: 6 }}>
                  <span
                    title={m.ktp}
                    style={{ color: scada.textMuted, fontSize: 10.5, fontWeight: 400, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >{m.ktp}</span>
                  <span
                    title={online ? i18n.t('ak_scada_updated_ago', { a: ago(m.ageSeconds) }) : i18n.t('ak_scada_offline_2', { s: m.status })}
                    style={{ width: 6, height: 6, borderRadius: 999, background: dotColor(m), display: 'inline-block', flex: '0 0 auto' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.1, color: online ? scada.text : scada.textMuted }}>
                    {mwh(faceWh)}
                  </span>
                  <span style={{ fontSize: 8, color: scada.textDim }}>{i18n.t('ak_scada_mwh')}</span>
                </div>
                <div style={{ color: scada.textMuted, fontSize: 9, lineHeight: 1.2 }}>
                  {online
                    ? `P ${m.pW != null ? (m.pW / 1000).toFixed(1) : '—'} ${i18n.t('ak_scada_kw')}`
                    : i18n.t('ak_scada_offline_3')}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
